<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import WhatsappQrChannel from 'dashboard/api/channel/whatsappQrChannel';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const POLL_INTERVAL_MS = 5000;

const router = useRouter();
const { t } = useI18n();

const qrCodeBase64 = ref('');
const connectionState = ref('');
const isLoading = ref(true);
const isCompleting = ref(false);
const hasCompleted = ref(false);
let pollTimer = null;

// Shared across component instances (e.g. Vue strict mode remount) to avoid duplicate complete calls.
const completionGuard = { inFlight: false, done: false };

const clearPollTimer = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const handleError = error => {
  const message =
    parseAPIErrorResponse(error) ||
    error?.response?.data?.error ||
    t('INBOX_MGMT.ADD.WHATSAPP_QR.ERROR_MESSAGE');
  useAlert(message);
};

async function completeConnection() {
  if (
    hasCompleted.value ||
    isCompleting.value ||
    completionGuard.done ||
    completionGuard.inFlight
  ) {
    return;
  }

  completionGuard.inFlight = true;
  isCompleting.value = true;
  clearPollTimer();

  try {
    const { data } = await WhatsappQrChannel.complete();
    hasCompleted.value = true;
    completionGuard.done = true;
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: data.id,
      },
    });
  } catch (error) {
    completionGuard.inFlight = false;
    isCompleting.value = false;
    clearPollTimer();
    // eslint-disable-next-line no-use-before-define -- mutual polling/complete callbacks
    pollTimer = setInterval(checkStatus, POLL_INTERVAL_MS);
    handleError(error);
  }
}

async function checkStatus() {
  try {
    const { data } = await WhatsappQrChannel.fetchStatus();
    connectionState.value = data.state;

    if (data.state === 'open') {
      await completeConnection();
    }
  } catch (error) {
    handleError(error);
  }
}

function startPolling() {
  clearPollTimer();
  pollTimer = setInterval(checkStatus, POLL_INTERVAL_MS);
}

const loadQr = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappQrChannel.fetchQr();
    qrCodeBase64.value = data.qr_base64 || '';
    connectionState.value = data.state || '';

    if (data.state === 'open') {
      await completeConnection();
      return;
    }

    startPolling();
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadQr();
});

onBeforeUnmount(() => {
  clearPollTimer();
});
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_QR.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_QR.DESC')"
    />

    <LoadingState
      v-if="isLoading || isCompleting"
      :message="
        isCompleting
          ? $t('INBOX_MGMT.ADD.WHATSAPP_QR.COMPLETING')
          : $t('INBOX_MGMT.ADD.WHATSAPP_QR.LOADING')
      "
    />

    <div v-else class="flex flex-col items-center gap-6 max-w-md mx-auto">
      <p class="text-sm text-n-slate-11 text-center">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.SCAN_INSTRUCTIONS') }}
      </p>

      <img
        v-if="qrCodeBase64"
        :src="qrCodeBase64"
        :alt="$t('INBOX_MGMT.ADD.WHATSAPP_QR.QR_ALT')"
        class="w-64 h-64 rounded-lg border border-n-weak"
      />

      <p
        v-if="connectionState && connectionState !== 'open'"
        class="text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.WAITING') }}
      </p>
    </div>
  </div>
</template>
